import { useState } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  Boxes,
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
  Plus,
  ScanLine,
  Settings2,
  Tags,
  UserRound,
  Users,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { LoginPage } from '../pages/LoginPage';
import { ScannerSheet } from './ScannerSheet';
import { Spinner, cn } from './ui';

/**
 * Rahmen der Anwendung.
 *
 * Mobil zuerst gedacht: unten die Daumenleiste mit den vier häufigsten Wegen
 * und dem Erfassen-Knopf in der Mitte, oben der Scanner. Ab Tablettbreite
 * wandert die Navigation an den linken Rand.
 */

const PUBLIC_PREFIXES = ['/anmelden', '/bestaetigung', '/einladung'];

interface NavItem {
  to: string;
  label: string;
  icon: typeof Boxes;
  permission?: Parameters<ReturnType<typeof useAuth>['can']>[0];
}

const PRIMARY_NAV: NavItem[] = [
  { to: '/', label: 'Übersicht', icon: LayoutDashboard },
  { to: '/geraete', label: 'Geräte', icon: Boxes },
  { to: '/personen', label: 'Personen', icon: Users },
  { to: '/inventur', label: 'Inventur', icon: ClipboardCheck, permission: 'inventory.read' },
];

const SECONDARY_NAV: NavItem[] = [
  { to: '/stammdaten', label: 'Stammdaten', icon: Tags, permission: 'stammdaten.read' },
  { to: '/verwaltung', label: 'Verwaltung', icon: Settings2, permission: 'admin.manage' },
];

export function AppShell() {
  const auth = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [scannerOpen, setScannerOpen] = useState(false);

  const isPublic = PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix));

  if (auth.isLoading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner label="Anmeldung wird geprüft …" />
      </div>
    );
  }

  if (isPublic) return <Outlet />;
  // Ohne Anmeldung — und ebenso, solange der zweite Faktor fehlt — bleibt die
  // Anwendung verborgen; die Anmeldeseite führt durch die Einrichtung.
  if (!auth.user || auth.user.setup_required) return <LoginPage />;

  const visible = (items: NavItem[]) =>
    items.filter((item) => !item.permission || auth.can(item.permission));

  const canCapture = auth.can('asset.write');

  return (
    <div className="min-h-dvh lg:flex">
      {/* ---------- Seitenleiste ab großer Breite ---------- */}
      <aside className="sticky top-0 hidden h-dvh w-72 shrink-0 flex-col border-r border-[var(--color-line)] bg-[var(--color-bg-tinted)]/70 px-4 py-7 lg:flex">
        <Brand />

        <nav className="mt-9 flex flex-1 flex-col gap-1.5">
          {visible(PRIMARY_NAV).map((item) => (
            <SidebarLink key={item.to} item={item} active={isActive(pathname, item.to)} />
          ))}

          {visible(SECONDARY_NAV).length > 0 && (
            <>
              <hr className="my-4 border-[var(--color-line)]" />
              {visible(SECONDARY_NAV).map((item) => (
                <SidebarLink key={item.to} item={item} active={isActive(pathname, item.to)} />
              ))}
            </>
          )}
        </nav>

        {canCapture && (
          <Link to="/erfassen" className="btn btn-accent btn-lg mb-4 w-full no-underline">
            <Plus className="h-5 w-5" aria-hidden />
            Gerät erfassen
          </Link>
        )}

        <div className="rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[var(--color-bg-elevated)] p-4">
          <Link to="/profil" className="block no-underline">
            <p className="truncate font-semibold text-[var(--color-fg)]">{auth.user.name}</p>
            <p className="mt-0.5 truncate text-sm text-[var(--color-fg3)]">
              {roleLabel(auth.user.role)}
            </p>
          </Link>
          <button
            type="button"
            onClick={() => void auth.logout()}
            className="btn btn-quiet btn-md mt-2.5 w-full justify-start px-3"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            Abmelden
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* ---------- Kopfzeile ---------- */}
        <header className="sticky top-0 z-30 border-b border-[var(--color-line)] bg-[var(--color-bg)]/85 backdrop-blur-sm">
          <div className="mx-auto flex h-18 max-w-7xl items-center gap-4 px-5 sm:px-7">
            <div className="lg:hidden">
              <Brand compact />
            </div>

            <div className="flex-1" />

            <button
              type="button"
              onClick={() => setScannerOpen(true)}
              className="btn btn-ghost btn-md"
              aria-label="Code scannen"
            >
              <ScanLine className="h-5 w-5" aria-hidden />
              <span className="hidden sm:inline">Scannen</span>
            </button>

            <Link
              to="/profil"
              className="hidden h-11 w-11 place-items-center rounded-full border border-[var(--color-line)] bg-[var(--color-bg-elevated)] no-underline lg:grid"
              aria-label="Profil"
            >
              <UserRound className="h-5 w-5 text-[var(--color-fg2)]" aria-hidden />
            </Link>
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 px-5 pt-8 pb-32 sm:px-7 lg:pb-16">
          <Outlet />
        </main>

        {/* ---------- Daumenleiste auf kleinen Geräten ---------- */}
        <nav className="safe-bottom fixed inset-x-0 bottom-0 z-30 border-t border-[var(--color-line)] bg-[var(--color-bg-elevated)]/95 backdrop-blur-sm lg:hidden">
          <div className="mx-auto grid max-w-xl grid-cols-5 items-end px-2 py-2.5">
            {visible(PRIMARY_NAV)
              .slice(0, 2)
              .map((item) => (
                <BottomLink key={item.to} item={item} active={isActive(pathname, item.to)} />
              ))}

            <div className="flex justify-center">
              {canCapture ? (
                <Link
                  to="/erfassen"
                  aria-label="Gerät erfassen"
                  className="grid h-16 w-16 -translate-y-4 place-items-center rounded-full bg-[var(--color-accent)] text-white no-underline shadow-[var(--shadow-md)] transition-transform duration-200 active:scale-95"
                >
                  <Plus className="h-7 w-7" aria-hidden />
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => setScannerOpen(true)}
                  aria-label="Code scannen"
                  className="grid h-16 w-16 -translate-y-4 place-items-center rounded-full bg-[var(--color-khadi-forest-800)] text-white shadow-[var(--shadow-md)] transition-transform duration-200 active:scale-95"
                >
                  <ScanLine className="h-7 w-7" aria-hidden />
                </button>
              )}
            </div>

            {visible(PRIMARY_NAV)
              .slice(2, 4)
              .map((item) => (
                <BottomLink key={item.to} item={item} active={isActive(pathname, item.to)} />
              ))}
          </div>
        </nav>
      </div>

      {scannerOpen && <ScannerSheet onClose={() => setScannerOpen(false)} />}
    </div>
  );
}

function Brand({ compact }: { compact?: boolean }) {
  return (
    <Link to="/" className="flex items-center gap-2.5 no-underline">
      {/* Das Zeichen greift das Gewebe auf: gekreuzte Fäden. */}
      <span
        aria-hidden
        className="grid h-8 w-8 place-items-center rounded-[var(--radius-sm)] bg-[var(--color-khadi-forest-800)]"
      >
        <span
          className="h-4 w-4 rounded-[1px] opacity-90"
          style={{
            backgroundImage:
              'repeating-linear-gradient(90deg, var(--color-khadi-cream-50) 0 1px, transparent 1px 3px), repeating-linear-gradient(0deg, var(--color-khadi-cream-50) 0 1px, transparent 1px 3px)',
          }}
        />
      </span>
      <span className="leading-tight">
        <span className="block font-[family-name:var(--font-display)] text-[1.0625rem] text-[var(--color-fg)]">
          Khadi Inventar
        </span>
        {!compact && (
          <span className="block text-[0.6875rem] tracking-[0.14em] text-[var(--color-fg3)] uppercase">
            Geräteverwaltung
          </span>
        )}
      </span>
    </Link>
  );
}

function SidebarLink({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      className={cn(
        'flex items-center gap-2.5 rounded-[var(--radius-md)] px-5 py-4 text-sm no-underline transition-colors duration-150',
        active
          ? 'bg-[var(--color-khadi-forest-800)] font-semibold text-[var(--color-khadi-cream-50)]'
          : 'text-[var(--color-fg2)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-fg)]',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      {item.label}
    </Link>
  );
}

function BottomLink({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      className={cn(
        'flex flex-col items-center gap-0.5 rounded-[var(--radius-md)] px-1 py-1.5 text-[0.6875rem] no-underline transition-colors duration-150',
        active ? 'text-[var(--color-accent)]' : 'text-[var(--color-fg3)]',
      )}
    >
      <Icon className={cn('h-5 w-5 transition-transform duration-200', active && 'scale-110')} aria-hidden />
      {item.label}
    </Link>
  );
}

function isActive(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

function roleLabel(role: string): string {
  return role === 'admin'
    ? 'Yagemi Admin'
    : role === 'manager'
      ? 'Khadi Manager'
      : 'Khadi Leser';
}
